import React, { useState } from 'react';
import { X, Search, Loader2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Word, Theme } from '../types';
import { fetchDefinition } from '../services/dictionary';
import toast from 'react-hot-toast';

interface AddWordFormProps {
  onAdd: (word: Word) => void;
  onCancel: () => void;
  theme: Theme;
}

function AddWordForm({ onAdd, onCancel, theme }: AddWordFormProps) {
  const [formData, setFormData] = useState({
    word: '',
    definition: '',
    source: '', // Restored source field
    synonyms: [] as string[],
    antonyms: [] as string[],
    learnerDefinition: '',
    intermediateDefinition: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(true);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.word || !formData.definition) return;
    
    onAdd({
      ...formData,
      date: new Date().toISOString(),
      mastery: 0,
      reviewCount: 0,
      difficulty: 'beginner',
      streak: 0
    });
    setShowTooltip(false);
  };

  const handleLookup = async () => {
    if (!formData.word.trim()) return;

    setIsLoading(true);
    try {
      const result = await fetchDefinition(formData.word.trim());
      setFormData(prev => ({
        ...prev,
        definition: result.definition,
        synonyms: result.thesaurus?.synonyms || [],
        antonyms: result.thesaurus?.antonyms || [],
        learnerDefinition: result.learners || '',
        intermediateDefinition: result.intermediate || '',
      }));
      toast.success('Definitions found!');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Failed to fetch definition');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      onSubmit={handleSubmit}
      className={`p-6 rounded-lg shadow-lg ${
        theme === 'dark' ? 'bg-gray-800' : 'bg-white'
      }`}
    >
      <div className="flex justify-between items-center mb-4">
        <h2 className={`text-xl font-semibold ${
          theme === 'dark' ? 'text-white' : 'text-gray-800'
        }`}>
          Add New Word
        </h2>
        <button
          type="button"
          onClick={onCancel}
          className={`text-gray-500 hover:text-gray-700 ${
            theme === 'dark' ? 'hover:text-gray-300' : ''
          }`}
        >
          <X size={20} />
        </button>
      </div>

      <div className="space-y-4">
        <div>
          <label htmlFor="word" className={`block text-sm font-medium mb-1 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Word
          </label>
          <div className="flex gap-2 relative">
            <input
              type="text"
              id="word"
              required
              value={formData.word}
              onChange={(e) => setFormData(prev => ({ ...prev, word: e.target.value }))}
              className={`flex-1 px-3 py-2 rounded-md ${
                theme === 'dark'
                  ? 'bg-gray-700 text-white border-gray-600'
                  : 'bg-white text-gray-900 border-gray-300'
              } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
            />
            <div className="relative">
              <button
                type="button"
                onClick={handleLookup}
                disabled={isLoading}
                className={`px-4 py-2 rounded-md flex items-center gap-2 ${
                  theme === 'dark'
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-purple-600 text-white hover:bg-purple-700'
                } disabled:opacity-50`}
              >
                {isLoading ? <Loader2 className="animate-spin" size={20} /> : <Search size={20} />}
                {showTooltip && formData.word === '' && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-gray-800 text-white text-sm rounded whitespace-nowrap"
                  >
                    Click to auto-fill definitions!
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800"></div>
                  </motion.div>
                )}
              </button>
            </div>
          </div>
        </div>

        <div>
          <label htmlFor="definition" className={`block text-sm font-medium mb-1 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Definition
          </label>
          <textarea
            id="definition"
            required
            value={formData.definition}
            onChange={(e) => setFormData(prev => ({ ...prev, definition: e.target.value }))}
            className={`w-full px-3 py-2 rounded-md ${
              theme === 'dark'
                ? 'bg-gray-700 text-white border-gray-600'
                : 'bg-white text-gray-900 border-gray-300'
            } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
            rows={3}
          />
        </div>

        <div>
          <label htmlFor="source" className={`block text-sm font-medium mb-1 ${
            theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
          }`}>
            Source
          </label>
          <input
            type="text"
            id="source"
            value={formData.source}
            onChange={(e) => setFormData(prev => ({ ...prev, source: e.target.value }))}
            className={`w-full px-3 py-2 rounded-md ${
              theme === 'dark'
                ? 'bg-gray-700 text-white border-gray-600'
                : 'bg-white text-gray-900 border-gray-300'
            } focus:ring-2 focus:ring-purple-500 focus:border-transparent`}
            placeholder="e.g., Book title, website, conversation"
          />
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={onCancel}
            className={`px-4 py-2 rounded-md ${
              theme === 'dark'
                ? 'bg-gray-700 text-white hover:bg-gray-600'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Cancel
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="submit"
            className="px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700"
          >
            Add Word
          </motion.button>
        </div>
      </div>
    </motion.form>
  );
}

export default AddWordForm;